import React from 'react';
import { Alert, Card, Collapse, Descriptions, Empty, Progress, Tag, Typography } from 'antd';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import {
  ValidatorReview,
  ValidatorReviewAttempt,
  ValidatorReviewCorrection,
  ValidatorReviewIssue,
  ValidatorReviewSalesforceResult,
} from '../types';
import { selectFieldsWithOptions } from './contants';

const { Panel } = Collapse;
const { Paragraph, Text } = Typography;

interface ValidatorAiReviewProps {
  review: ValidatorReview | null;
}

type ValidatorMappedItem = Pick<
  ValidatorReviewIssue,
  'locale_key' | 'schema_id' | 'section_schema_id' | 'line_item_index' | 'agent_field' | 'agent_block' | 'json_path'
> & {
  field?: string;
};

const formatConfidence = (confidence: number) => `${Math.round(confidence * 100)}%`;

const formatReviewRoute = (route?: string) => (route ? route.replace(/_/g, ' ') : '—');

const formatMessageId = (intl: IntlShape, id?: string) => {
  if (!id?.trim()) return null;
  return intl.formatMessage({ id: id.trim(), defaultMessage: id.trim() });
};

const hasIntlMessage = (intl: IntlShape, id: string) =>
  Object.prototype.hasOwnProperty.call(intl.messages, id);

const getValidatorFieldSchemaKey = (item: ValidatorMappedItem) =>
  item.locale_key?.trim()
  || item.schema_id?.trim()
  || item.agent_field?.trim()
  || ('field' in item && item.field ? item.field.trim() : '')
  || null;

const getValidatorLocaleKey = (intl: IntlShape, item: ValidatorMappedItem) => {
  const key = getValidatorFieldSchemaKey(item);
  if (!key) return null;
  return formatMessageId(intl, key);
};

const isValidatorItemMapped = (item: ValidatorMappedItem) => !!getValidatorFieldSchemaKey(item);

const getValidatorFieldLabel = (intl: IntlShape, item: ValidatorMappedItem) =>
  getValidatorLocaleKey(intl, item);

const enhanceTextWithFieldLabel = (
  text: string,
  item: ValidatorMappedItem,
  fieldLabel: string,
) => {
  const rawNames = [item.agent_field, item.schema_id, item.locale_key, 'field' in item ? item.field : undefined]
    .filter((name): name is string => !!name);

  let result = text;
  rawNames.forEach(name => {
    if (name !== fieldLabel) {
      result = result.split(name).join(fieldLabel);
    }
  });
  return result;
};

const formatValidatorCode = (intl: IntlShape, code: string) => intl.formatMessage({
  id: `document.processing.validatorReview.code.${code}`,
  defaultMessage: code.replace(/_/g, ' '),
});

const formatValidatorIssueMessage = (intl: IntlShape, issue: ValidatorReviewIssue) => {
  const fieldLabel = getValidatorFieldLabel(intl, issue);
  const issueMessageId = `document.processing.validatorReview.issue.${issue.code}`;

  if (fieldLabel) {
    if (hasIntlMessage(intl, issueMessageId)) {
      return intl.formatMessage(
        { id: issueMessageId, defaultMessage: issue.message },
        { field: fieldLabel },
      );
    }
    return enhanceTextWithFieldLabel(issue.message, issue, fieldLabel);
  }

  return issue.message;
};

const formatValidatorCorrectionInstruction = (
  intl: IntlShape,
  correction: ValidatorReviewCorrection,
) => {
  const fieldLabel = getValidatorFieldLabel(intl, correction);
  const correctionMessageId = `document.processing.validatorReview.correction.${correction.issue}`;

  if (fieldLabel) {
    if (hasIntlMessage(intl, correctionMessageId)) {
      return intl.formatMessage(
        { id: correctionMessageId, defaultMessage: correction.instruction },
        { field: fieldLabel },
      );
    }
    return enhanceTextWithFieldLabel(correction.instruction, correction, fieldLabel);
  }

  return correction.instruction;
};

const formatEnumValueLabel = (intl: IntlShape, fieldKey: string | null | undefined, value: string) => {
  if (!value) return value;

  const lookupKeys = [fieldKey, fieldKey?.toLowerCase()].filter(Boolean) as string[];
  for (const key of lookupKeys) {
    const options = selectFieldsWithOptions(intl, key);
    const match = options.find(option => String(option.value) === value);
    if (match?.label) {
      return String(match.label);
    }
  }

  return intl.formatMessage({ id: value, defaultMessage: value });
};

const formatSuggestedValue = (
  intl: IntlShape,
  correction: ValidatorReviewCorrection,
) => {
  const value = correction.suggested_value;
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  const stringValue = String(value);
  if (correction.expected_type === 'enum' || correction.allowed_values?.length) {
    return formatEnumValueLabel(intl, getValidatorFieldSchemaKey(correction), stringValue);
  }

  return stringValue;
};

const severityColor = (severity: string) => {
  if (severity === 'blocking') return 'error';
  if (severity === 'warning') return 'warning';
  return 'default';
};

const ValidatorMappingDetails: React.FC<{ item: ValidatorMappedItem }> = ({ item }) => {
  const intl = useIntl();
  const sectionLabel = item.section_schema_id
    ? formatMessageId(intl, item.section_schema_id)
    : null;

  if (!sectionLabel && item.line_item_index === undefined && !item.agent_field && !item.agent_block) {
    return null;
  }

  return (
    <Descriptions size="small" column={1} bordered style={{ marginBottom: 8 }}>
      {sectionLabel && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.section' })}>
          {sectionLabel}
        </Descriptions.Item>
      )}
      {item.line_item_index !== undefined && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.lineItemIndex' })}>
          {intl.formatMessage(
            { id: 'document.processing.validatorReview.lineItemIndexValue' },
            { index: item.line_item_index + 1 },
          )}
        </Descriptions.Item>
      )}
      {item.agent_field && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.agentField' })}>
          {formatMessageId(intl, item.agent_field) ?? item.agent_field}
        </Descriptions.Item>
      )}
      {item.agent_block && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.agentBlock' })}>
          {item.agent_block}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

const IssueList: React.FC<{ issues: ValidatorReviewIssue[] }> = ({ issues }) => {
  const intl = useIntl();

  if (!issues.length) {
    return (
      <Text type="secondary">
        {intl.formatMessage({ id: 'document.processing.validatorReview.none' })}
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {issues.map((issue, index) => {
        const mapped = isValidatorItemMapped(issue);
        const fieldLabel = getValidatorFieldLabel(intl, issue);

        return (
          <Card key={`${issue.code}-${index}`} size="small" style={{ background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Tag color={severityColor(issue.severity)}>{formatValidatorCode(intl, issue.code)}</Tag>
              <Tag>{issue.severity}</Tag>
              {fieldLabel && <Tag color="blue">{fieldLabel}</Tag>}
            </div>
            {mapped && <ValidatorMappingDetails item={issue} />}
            <Paragraph style={{ marginBottom: 8 }}>{formatValidatorIssueMessage(intl, issue)}</Paragraph>
            <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
              {intl.formatMessage({ id: 'document.processing.validatorReview.jsonPath' })}: {issue.json_path}
            </Text>
          </Card>
        );
      })}
    </div>
  );
};

const CorrectionList: React.FC<{ corrections: ValidatorReviewCorrection[] }> = ({ corrections }) => {
  const intl = useIntl();

  if (!corrections.length) {
    return (
      <Text type="secondary">
        {intl.formatMessage({ id: 'document.processing.validatorReview.none' })}
      </Text>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {corrections.map((correction, index) => {
        const fieldLabel = getValidatorFieldLabel(intl, correction) || correction.field;

        return (
          <Card key={`${correction.field}-${index}`} size="small" style={{ background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Tag color="blue">{fieldLabel}</Tag>
              <Tag>{formatValidatorCode(intl, correction.issue)}</Tag>
              {correction.auto_apply && <Tag color="green">auto apply</Tag>}
            </div>
            {isValidatorItemMapped(correction) && <ValidatorMappingDetails item={correction} />}
            <Paragraph style={{ marginBottom: 8 }}>{formatValidatorCorrectionInstruction(intl, correction)}</Paragraph>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.suggestedValue' })}>
                {formatSuggestedValue(intl, correction)}
              </Descriptions.Item>
              <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.confidence' })}>
                {formatConfidence(correction.confidence)}
              </Descriptions.Item>
              <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.expectedType' })}>
                {correction.expected_type}
              </Descriptions.Item>
              {correction.allowed_values?.length > 0 && (
                <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.allowedValues' })}>
                  {correction.allowed_values
                    .map(value => formatEnumValueLabel(intl, getValidatorFieldSchemaKey(correction), value))
                    .join(', ')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.jsonPath' })}>
                <Text type="secondary" style={{ wordBreak: 'break-all' }}>{correction.json_path}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        );
      })}
    </div>
  );
};

const RetryDetails: React.FC<{ retry: ValidatorReview['retry'] }> = ({ retry }) => {
  const intl = useIntl();

  if (!retry?.eligible && !retry?.reason && !retry?.agent_prompt_addendum) {
    return null;
  }

  return (
    <Descriptions size="small" column={1} bordered>
      <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.retryEligible' })}>
        {retry.eligible
          ? intl.formatMessage({ id: 'document.processing.validatorReview.yes' })
          : intl.formatMessage({ id: 'document.processing.validatorReview.no' })}
      </Descriptions.Item>
      {retry.reason && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.retryReason' })}>
          {retry.reason}
        </Descriptions.Item>
      )}
      {retry.agent_prompt_addendum && (
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.retryPrompt' })}>
          {retry.agent_prompt_addendum}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

const getSalesforceErrorMessage = (result: ValidatorReviewSalesforceResult): string | null => {
  return result.response?.message
    || result.response?.messageAi
    || result.error
    || null;
};

const isSalesforceFailure = (result: ValidatorReviewSalesforceResult): boolean => {
  if (result.status === 'failed' || result.status === 'error') {
    return true;
  }
  if (result.response?.success === false) {
    return true;
  }
  if (result.http_status && result.http_status >= 400) {
    return true;
  }
  return false;
};

const SalesforceResultPanel: React.FC<{ result: ValidatorReviewSalesforceResult }> = ({ result }) => {
  const intl = useIntl();
  const failed = isSalesforceFailure(result);
  const errorMessage = getSalesforceErrorMessage(result);

  return (
    <Card
      title={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceResult' })}
      style={{ marginBottom: 16 }}
    >
      {failed && errorMessage && (
        <Alert
          type="error"
          showIcon
          message={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceError' })}
          description={errorMessage}
          style={{ marginBottom: 16 }}
        />
      )}
      {!failed && result.status && (
        <Alert
          type="success"
          showIcon
          message={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceSuccess' })}
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceStatus' })}>
          <Tag color={failed ? 'error' : 'success'}>{result.status}</Tag>
        </Descriptions.Item>
        {result.document_id && (
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceDocumentId' })}>
            {result.document_id}
          </Descriptions.Item>
        )}
        {result.http_status !== undefined && (
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceHttpStatus' })}>
            {result.http_status}
          </Descriptions.Item>
        )}
        {result.response?.errorcode !== undefined && (
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceErrorCode' })}>
            {result.response.errorcode}
          </Descriptions.Item>
        )}
        {result.error && errorMessage !== result.error && (
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceErrorDetail' })}>
            {result.error}
          </Descriptions.Item>
        )}
        {result.instance_url && (
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.salesforceInstanceUrl' })}>
            <a href={result.instance_url} target="_blank" rel="noopener noreferrer">
              {result.instance_url}
            </a>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

const AttemptPanel: React.FC<{ attempt: ValidatorReviewAttempt }> = ({ attempt }) => {
  const intl = useIntl();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card size="small">
        <Paragraph style={{ marginBottom: 12 }}>{attempt.summary}</Paragraph>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.valid' })}>
            <Tag color={attempt.valid ? 'success' : 'error'}>
              {attempt.valid
                ? intl.formatMessage({ id: 'document.processing.validatorReview.yes' })
                : intl.formatMessage({ id: 'document.processing.validatorReview.no' })}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.autoSubmit' })}>
            <Tag color={attempt.auto_submit_eligible ? 'success' : 'default'}>
              {attempt.auto_submit_eligible
                ? intl.formatMessage({ id: 'document.processing.validatorReview.yes' })
                : intl.formatMessage({ id: 'document.processing.validatorReview.no' })}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.confidence' })}>
            {formatConfidence(attempt.confidence)}
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.reviewRoute' })}>
            {formatReviewRoute(attempt.review_route)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Collapse>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.blockingIssues' })} (${attempt.blocking_issues.length})`}
          key="blocking"
        >
          <IssueList issues={attempt.blocking_issues} />
        </Panel>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.warnings' })} (${attempt.warnings.length})`}
          key="warnings"
        >
          <IssueList issues={attempt.warnings} />
        </Panel>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.corrections' })} (${attempt.corrections.length})`}
          key="corrections"
        >
          <CorrectionList corrections={attempt.corrections} />
        </Panel>
        {(attempt.retry?.eligible || attempt.retry?.reason) && (
          <Panel header={intl.formatMessage({ id: 'document.processing.validatorReview.retry' })} key="retry">
            <RetryDetails retry={attempt.retry} />
          </Panel>
        )}
      </Collapse>
    </div>
  );
};

const ValidatorAiReview: React.FC<ValidatorAiReviewProps> = ({ review }) => {
  const intl = useIntl();

  if (!review) {
    return (
      <div style={{ height: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty description={intl.formatMessage({ id: 'document.processing.validatorReview.empty' })} />
      </div>
    );
  }

  return (
    <div style={{ height: '90vh', overflowY: 'auto', padding: '8px 0 24px' }}>
      <Card title={intl.formatMessage({ id: 'document.processing.validatorReview.summaryTitle' })} style={{ marginBottom: 16 }}>
        <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>{review.summary}</Paragraph>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Tag color={review.valid ? 'success' : 'error'}>
            {review.valid
              ? intl.formatMessage({ id: 'document.processing.validatorReview.validDocument' })
              : intl.formatMessage({ id: 'document.processing.validatorReview.invalidDocument' })}
          </Tag>
          {review.auto_submit_eligible && (
            <Tag color="processing">
              {intl.formatMessage({ id: 'document.processing.validatorReview.autoSubmitEligible' })}
            </Tag>
          )}
          <Tag>{formatReviewRoute(review.review_route)}</Tag>
          <Tag>
            {intl.formatMessage(
              { id: 'document.processing.validatorReview.attemptLabel' },
              { current: review.attempt_number, max: review.max_attempts },
            )}
          </Tag>
        </div>

        <div style={{ maxWidth: 280, marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            {intl.formatMessage({ id: 'document.processing.validatorReview.confidence' })}
          </Text>
          <Progress percent={Math.round(review.confidence * 100)} size="small" />
        </div>

        <Descriptions size="small" column={2}>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.blockingIssues' })}>
            {review.blocking_issues.length}
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.warnings' })}>
            {review.warnings.length}
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.corrections' })}>
            {review.corrections.length}
          </Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'document.processing.validatorReview.validationAttempts' })}>
            {review.validation_attempts?.length ?? 0}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {review.salesforce_result && (
        <SalesforceResultPanel result={review.salesforce_result} />
      )}

      <Collapse style={{ marginBottom: 16 }}>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.blockingIssues' })} (${review.blocking_issues.length})`}
          key="blocking"
        >
          <IssueList issues={review.blocking_issues} />
        </Panel>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.warnings' })} (${review.warnings.length})`}
          key="warnings"
        >
          <IssueList issues={review.warnings} />
        </Panel>
        <Panel
          header={`${intl.formatMessage({ id: 'document.processing.validatorReview.corrections' })} (${review.corrections.length})`}
          key="corrections"
        >
          <CorrectionList corrections={review.corrections} />
        </Panel>
        {(review.retry?.eligible || review.retry?.reason) && (
          <Panel header={intl.formatMessage({ id: 'document.processing.validatorReview.retry' })} key="retry">
            <RetryDetails retry={review.retry} />
          </Panel>
        )}
      </Collapse>

      {(review.validation_attempts?.length ?? 0) > 0 && (
        <Card title={intl.formatMessage({ id: 'document.processing.validatorReview.validationAttempts' })}>
          <Collapse>
            {(review.validation_attempts ?? []).map(attempt => (
              <Panel
                key={`attempt-${attempt.attempt_number}`}
                header={intl.formatMessage(
                  { id: 'document.processing.validatorReview.attemptLabel' },
                  { current: attempt.attempt_number, max: attempt.max_attempts },
                )}
              >
                <AttemptPanel attempt={attempt} />
              </Panel>
            ))}
          </Collapse>
        </Card>
      )}
    </div>
  );
};

export default ValidatorAiReview;
